import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes, type ReactNode } from "react";
import { Dialog as RxDialog, Direction as RxDirection, DropdownMenu as RxDropdown, HoverCard as RxHoverCard, Popover as RxPopover, Tooltip as RxTooltip, Tabs as RxTabs, ScrollArea as RxScroll, Separator as RxSeparator, Avatar as RxAvatar, Progress as RxProgress } from "radix-ui";
import { X } from "lucide-react";
import { cn } from "../lib/cn";
import { microcopy } from "../lib/microcopy";
import { ActionScope } from "./button";

/**
 * Where overlays sit. A page in an auto-height frame (a matter tab, a home widget) is as tall as its content, so the host passes the
 * part of the frame on screen as --ncp-vtop and --ncp-vh; elsewhere they are unset and overlays fill the viewport as usual.
 */
const VISIBLE_CENTRE = "calc(var(--ncp-vtop, 0px) + var(--ncp-vh, 100%) / 2)";
const VISIBLE_SIDE = { top: "var(--ncp-vtop, 0px)", height: "var(--ncp-vh, 100%)" } as const;
const VISIBLE_BOTTOM = { bottom: "calc(100% - var(--ncp-vtop, 0px) - var(--ncp-vh, 100%))", maxHeight: "calc(var(--ncp-vh, 100vh) * 0.85)" } as const;

// ---------------------------------------------------------------- Dialog
export const Dialog = RxDialog.Root;
export const DialogTrigger = RxDialog.Trigger;
export const DialogClose = RxDialog.Close;

export const DialogContent = forwardRef<ElementRef<typeof RxDialog.Content>, ComponentPropsWithoutRef<typeof RxDialog.Content> & { size?: "sm" | "md" | "lg" | "xl" | "full"; /** The content places its own close control. */ hideClose?: boolean }>(
  ({ className, children, size = "md", hideClose = false, style, ...props }, ref) => (
    <RxDialog.Portal>
      <RxDialog.Overlay className="fixed inset-0 z-50 bg-aeblack-950/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
      <RxDialog.Content
        ref={ref}
        style={{ top: VISIBLE_CENTRE, ...(size === "full" ? { height: "calc(var(--ncp-vh, 100vh) * 0.85)" } : null), ...style }}
        className={cn(
          "fixed start-1/2 top-1/2 z-50 grid w-[calc(100%-2rem)] -translate-x-1/2 rtl:translate-x-1/2 -translate-y-1/2 gap-4 rounded-2xl border border-sand-200 bg-card p-6 shadow-sand-lg duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          size === "sm" && "max-w-md",
          size === "md" && "max-w-lg",
          size === "lg" && "max-w-2xl",
          size === "xl" && "max-w-4xl",
          size === "full" && "max-w-[90vw] h-[85vh]",
          className,
        )}
        {...props}
      >
        <ActionScope emphasis="primary">{children}</ActionScope>
        {hideClose ? null : (
          <RxDialog.Close className="absolute end-4 top-4 rounded-md p-1 text-sand-600 opacity-70 transition-opacity hover:opacity-100 hover:bg-sand-100 focus:outline-none focus:ring-2 focus:ring-ring">
            <X className="size-4" />
            <span className="sr-only">{microcopy("close")}</span>
          </RxDialog.Close>
        )}
      </RxDialog.Content>
    </RxDialog.Portal>
  ),
);
DialogContent.displayName = "DialogContent";

export const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-1.5 text-start", className)} {...props} />
);
export const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />
);
export const DialogTitle = forwardRef<ElementRef<typeof RxDialog.Title>, ComponentPropsWithoutRef<typeof RxDialog.Title>>(({ className, ...props }, ref) => (
  <RxDialog.Title ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
));
DialogTitle.displayName = "DialogTitle";
export const DialogDescription = forwardRef<ElementRef<typeof RxDialog.Description>, ComponentPropsWithoutRef<typeof RxDialog.Description>>(({ className, ...props }, ref) => (
  <RxDialog.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = "DialogDescription";

// ---------------------------------------------------------------- Sheet (side drawer)
export const Sheet = RxDialog.Root;
export const SheetTrigger = RxDialog.Trigger;
export const SheetClose = RxDialog.Close;

/** `emphasis="secondary"` for panels that show a part of the page beside it (the case file, the history) rather than a task of their own. */
export const SheetContent = forwardRef<ElementRef<typeof RxDialog.Content>, ComponentPropsWithoutRef<typeof RxDialog.Content> & { side?: "start" | "end" | "bottom"; width?: string; emphasis?: "primary" | "secondary" }>(
  ({ className, children, side = "end", width = "max-w-xl", emphasis = "primary", style, onOpenAutoFocus, ...props }, ref) => (
    <RxDialog.Portal>
      <RxDialog.Overlay className="fixed inset-0 z-50 bg-aeblack-950/30 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
      <RxDialog.Content
        ref={ref}
        style={{ ...(side === "bottom" ? VISIBLE_BOTTOM : VISIBLE_SIDE), ...style }}
        // A side panel opens with the panel itself focused (its title is announced), not its first button: that button's tooltip and
        // focus ring would otherwise show the moment the panel opens. Tab goes on to the first control as before.
        onOpenAutoFocus={onOpenAutoFocus ?? ((e) => { e.preventDefault(); (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true }); })}
        className={cn(
          "fixed z-50 flex flex-col gap-4 bg-card shadow-sand-lg transition ease-out-expo data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-400",
          side === "end" && `inset-y-0 end-0 h-full w-full border-s border-sand-200 ${width} data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right rtl:data-[state=closed]:slide-out-to-left rtl:data-[state=open]:slide-in-from-left`,
          side === "start" && `inset-y-0 start-0 h-full w-full border-e border-sand-200 ${width} data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left rtl:data-[state=closed]:slide-out-to-right rtl:data-[state=open]:slide-in-from-right`,
          side === "bottom" && "inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl border-t border-sand-200 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          className,
        )}
        {...props}
      >
        <ActionScope emphasis={emphasis}>{children}</ActionScope>
        <RxDialog.Close className="absolute end-4 top-4 rounded-md p-1 text-sand-600 opacity-70 transition-opacity hover:opacity-100 hover:bg-sand-100 focus:outline-none focus:ring-2 focus:ring-ring">
          <X className="size-4" />
          <span className="sr-only">{microcopy("close")}</span>
        </RxDialog.Close>
      </RxDialog.Content>
    </RxDialog.Portal>
  ),
);
SheetContent.displayName = "SheetContent";
export const SheetHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={cn("flex flex-col gap-1.5 p-6 pb-0 text-start", className)} {...props} />;
export const SheetTitle = DialogTitle;
export const SheetDescription = DialogDescription;
export const SheetBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={cn("flex-1 overflow-y-auto scrollbar-thin px-6 pb-6", className)} {...props} />;

// ---------------------------------------------------------------- Tooltip
export const TooltipProvider = RxTooltip.Provider;

/**
 * The root every app renders its screens in. Radix primitives (tabs, menus, radio groups, sliders) take their reading direction from
 * here, not from the page: without it they render left-to-right inside an Arabic page.
 */
export function UiProvider({ dir, children }: { dir: "rtl" | "ltr"; children: ReactNode }) {
  return (
    <RxDirection.Provider dir={dir}>
      <RxTooltip.Provider>{children}</RxTooltip.Provider>
    </RxDirection.Provider>
  );
}
export function Tooltip({ content, children, side = "top" }: { content: ReactNode; children: ReactNode; side?: "top" | "bottom" | "left" | "right" }) {
  return (
    <RxTooltip.Root delayDuration={200}>
      <RxTooltip.Trigger asChild>{children}</RxTooltip.Trigger>
      <RxTooltip.Portal>
        <RxTooltip.Content side={side} sideOffset={6} className="z-50 max-w-xs rounded-lg bg-aeblack-950 px-3 py-1.5 text-xs text-white shadow-md animate-in fade-in-0 zoom-in-95">
          {content}
          <RxTooltip.Arrow className="fill-aeblack-950" />
        </RxTooltip.Content>
      </RxTooltip.Portal>
    </RxTooltip.Root>
  );
}

// ---------------------------------------------------------------- Popover
export const Popover = RxPopover.Root;
export const PopoverTrigger = RxPopover.Trigger;
export const PopoverContent = forwardRef<ElementRef<typeof RxPopover.Content>, ComponentPropsWithoutRef<typeof RxPopover.Content>>(({ className, align = "center", sideOffset = 6, ...props }, ref) => (
  <RxPopover.Portal>
    <RxPopover.Content ref={ref} align={align} sideOffset={sideOffset} className={cn("z-50 w-72 rounded-xl border border-sand-200 bg-popover p-4 shadow-sand-lg outline-none animate-in fade-in-0 zoom-in-95", className)} {...props} />
  </RxPopover.Portal>
));
PopoverContent.displayName = "PopoverContent";

// ---------------------------------------------------------------- Hover card
/** A preview on hover or keyboard focus (a citation's excerpt, a figure's provenance); the trigger keeps its own click action. */
export function HoverCard({ openDelay = 200, closeDelay = 100, ...props }: ComponentPropsWithoutRef<typeof RxHoverCard.Root>) {
  return <RxHoverCard.Root openDelay={openDelay} closeDelay={closeDelay} {...props} />;
}
export const HoverCardTrigger = RxHoverCard.Trigger;
export const HoverCardContent = forwardRef<ElementRef<typeof RxHoverCard.Content>, ComponentPropsWithoutRef<typeof RxHoverCard.Content>>(({ className, align = "start", sideOffset = 6, collisionPadding = 12, ...props }, ref) => (
  <RxHoverCard.Portal>
    <RxHoverCard.Content ref={ref} align={align} sideOffset={sideOffset} collisionPadding={collisionPadding} className={cn("z-50 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-sand-200 bg-popover p-3 text-start shadow-sand-lg outline-none animate-in fade-in-0 zoom-in-95", className)} {...props} />
  </RxHoverCard.Portal>
));
HoverCardContent.displayName = "HoverCardContent";

// ---------------------------------------------------------------- Dropdown menu
export const DropdownMenu = RxDropdown.Root;
export const DropdownMenuTrigger = RxDropdown.Trigger;
export const DropdownMenuGroup = RxDropdown.Group;
export const DropdownMenuContent = forwardRef<ElementRef<typeof RxDropdown.Content>, ComponentPropsWithoutRef<typeof RxDropdown.Content>>(({ className, sideOffset = 6, ...props }, ref) => (
  <RxDropdown.Portal>
    <RxDropdown.Content ref={ref} sideOffset={sideOffset} className={cn("z-50 min-w-48 overflow-hidden rounded-xl border border-sand-200 bg-popover p-1.5 shadow-sand-lg animate-in fade-in-0 zoom-in-95", className)} {...props} />
  </RxDropdown.Portal>
));
DropdownMenuContent.displayName = "DropdownMenuContent";
export const DropdownMenuItem = forwardRef<ElementRef<typeof RxDropdown.Item>, ComponentPropsWithoutRef<typeof RxDropdown.Item> & { destructive?: boolean }>(({ className, destructive, ...props }, ref) => (
  <RxDropdown.Item ref={ref} className={cn("relative flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none transition-colors focus:bg-sand-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:text-sand-600", destructive && "text-danger-700 focus:bg-danger-50", className)} {...props} />
));
DropdownMenuItem.displayName = "DropdownMenuItem";
export const DropdownMenuLabel = forwardRef<ElementRef<typeof RxDropdown.Label>, ComponentPropsWithoutRef<typeof RxDropdown.Label>>(({ className, ...props }, ref) => (
  <RxDropdown.Label ref={ref} className={cn("px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-sand-600", className)} {...props} />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";
export const DropdownMenuSeparator = forwardRef<ElementRef<typeof RxDropdown.Separator>, ComponentPropsWithoutRef<typeof RxDropdown.Separator>>(({ className, ...props }, ref) => (
  <RxDropdown.Separator ref={ref} className={cn("-mx-1 my-1 h-px bg-sand-200", className)} {...props} />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

// ---------------------------------------------------------------- Tabs
export const Tabs = RxTabs.Root;
export const TabsList = forwardRef<ElementRef<typeof RxTabs.List>, ComponentPropsWithoutRef<typeof RxTabs.List> & { variant?: "pill" | "underline" }>(({ className, variant = "pill", ...props }, ref) => (
  <RxTabs.List
    ref={ref}
    className={cn(
      variant === "pill" && "inline-flex h-10 items-center gap-1 rounded-xl bg-sand-100 p-1 text-muted-foreground",
      variant === "underline" && "flex items-center gap-1 border-b border-sand-200 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = "TabsList";
export const TabsTrigger = forwardRef<ElementRef<typeof RxTabs.Trigger>, ComponentPropsWithoutRef<typeof RxTabs.Trigger> & { variant?: "pill" | "underline" }>(({ className, variant = "pill", ...props }, ref) => (
  <RxTabs.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4",
      variant === "pill" && "rounded-lg px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-aegold-800 data-[state=active]:shadow-sm",
      variant === "underline" && "relative -mb-px border-b-2 border-transparent px-3 py-2.5 text-muted-foreground hover:text-foreground data-[state=active]:border-aegold-600 data-[state=active]:text-aegold-800",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = "TabsTrigger";
export const TabsContent = forwardRef<ElementRef<typeof RxTabs.Content>, ComponentPropsWithoutRef<typeof RxTabs.Content>>(({ className, ...props }, ref) => (
  <RxTabs.Content ref={ref} className={cn("mt-4 focus-visible:outline-none data-[state=active]:animate-fade-in", className)} {...props} />
));
TabsContent.displayName = "TabsContent";

// ---------------------------------------------------------------- Misc
export const ScrollArea = forwardRef<ElementRef<typeof RxScroll.Root>, ComponentPropsWithoutRef<typeof RxScroll.Root>>(({ className, children, ...props }, ref) => (
  <RxScroll.Root ref={ref} className={cn("relative overflow-hidden", className)} {...props}>
    <RxScroll.Viewport className="size-full rounded-[inherit]">{children}</RxScroll.Viewport>
    <RxScroll.Scrollbar orientation="vertical" className="flex touch-none select-none p-0.5 transition-colors w-2">
      <RxScroll.Thumb className="relative flex-1 rounded-full bg-sand-400/70" />
    </RxScroll.Scrollbar>
    <RxScroll.Corner />
  </RxScroll.Root>
));
ScrollArea.displayName = "ScrollArea";

export const Separator = forwardRef<ElementRef<typeof RxSeparator.Root>, ComponentPropsWithoutRef<typeof RxSeparator.Root>>(({ className, orientation = "horizontal", decorative = true, ...props }, ref) => (
  <RxSeparator.Root ref={ref} decorative={decorative} orientation={orientation} className={cn("shrink-0 bg-sand-200", orientation === "horizontal" ? "h-px w-full" : "h-full w-px", className)} {...props} />
));
Separator.displayName = "Separator";

const HONORIFICS = /^(د\.?|دكتور|م\.|أ\.|البروفيسور|Dr\.?|Prof\.?|Mr\.?|Mrs\.?|Ms\.?|Eng\.?|H\.?E\.?)$/i;

/** Two initials from the first and last name, without honorifics; Arabic letters are kept apart (ZWNJ) so they do not join into a word. */
export function initialsOf(name: string): string {
  const words = name.split(/\s+/).filter((w) => w && !HONORIFICS.test(w));
  const picked = words.length > 1 ? [words[0], words[words.length - 1]] : words;
  const arabic = /[\u0600-\u06FF]/.test(name);
  const first = (w: string) => (arabic && w.startsWith("ال") && w.length > 3 ? w.slice(2) : w)[0]?.toUpperCase() ?? "";
  return picked.map(first).join(arabic ? "\u200C" : "");
}

export function Avatar({ name, src, className, size = "md" }: { name: string; src?: string | null; className?: string; size?: "sm" | "md" | "lg" }) {
  const initials = initialsOf(name);
  return (
    <RxAvatar.Root className={cn("relative flex shrink-0 overflow-hidden rounded-full ring-2 ring-white", size === "sm" && "size-7 text-[10px]", size === "md" && "size-9 text-xs", size === "lg" && "size-12 text-sm", className)}>
      {src ? <RxAvatar.Image src={src} alt={name} className="aspect-square size-full object-cover" /> : null}
      <RxAvatar.Fallback data-slot="avatar-fallback" delayMs={src ? 300 : 0} className="flex size-full items-center justify-center bg-gradient-to-br from-aegold-200 to-camel-300 font-semibold text-aegold-900">
        {initials}
      </RxAvatar.Fallback>
    </RxAvatar.Root>
  );
}

export function Progress({ value, className, indicatorClassName, tone = "gold" }: { value: number; className?: string; indicatorClassName?: string; tone?: "gold" | "success" | "warning" | "danger" | "info" }) {
  const toneClass = { gold: "bg-aegold-600", success: "bg-success-600", warning: "bg-warning-500", danger: "bg-danger-600", info: "bg-seablue-500" }[tone];
  return (
    <RxProgress.Root value={value} className={cn("relative h-2 w-full overflow-hidden rounded-full bg-sand-200", className)}>
      <RxProgress.Indicator className={cn("h-full rounded-full transition-all duration-700 ease-out-expo", toneClass, indicatorClassName)} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </RxProgress.Root>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton h-4 w-full", className)} />;
}
